/**
 * The court's conditions and their deadlines on a case (Lloyd, Aug 21 2026).
 *
 * A deferral or DSC dismissal is conditional — a course, a certificate, a driving record, money,
 * hours — and the charge is dismissed only if each lands on time. This is the structured record of
 * those, and it is what the closing letter is made of: the letter refuses to generate while any
 * deadline is missing, so `readiness` comes back beside the list.
 */

export type ConditionType =
  | 'DSC_COURSE'
  | 'COURSE_CERTIFICATE'
  | 'DRIVING_RECORD'
  | 'AFFIDAVIT'
  | 'PAYMENT'
  | 'COMMUNITY_SERVICE'
  | 'OTHER';

export const CONDITION_TYPE_LABELS: Record<ConditionType, string> = {
  DSC_COURSE: 'Driving safety course',
  COURSE_CERTIFICATE: 'Certificate of completion to the court',
  DRIVING_RECORD: 'Certified driving record (DPS Type 3A) to the court',
  AFFIDAVIT: 'Sworn affidavit, notarized and returned',
  PAYMENT: 'Payment to the court',
  COMMUNITY_SERVICE: 'Community service + proof filed',
  OTHER: 'Other condition — see detail',
};

export const CONDITION_TYPES = Object.keys(CONDITION_TYPE_LABELS) as ConditionType[];

export interface CaseConditionDto {
  CaseCondition_guid: string;
  CaseCondition_Type: string;
  CaseCondition_DueAt: string | null;
  CaseCondition_AmountCents: number | null;
  CaseCondition_Hours: number | null;
  CaseCondition_Detail: string | null;
  CaseCondition_CompletedAt: string | null;
  CaseCondition_RecordedBy: string | null;
}

export interface ClosingLetterBlocker {
  index: number | null;
  reason: string;
}

export interface ClosingLetterReadiness {
  canGenerate: boolean;
  blockers: ClosingLetterBlocker[];
}

export interface ConditionsResponse {
  conditions: CaseConditionDto[];
  readiness: ClosingLetterReadiness;
}

export interface CreateConditionInput {
  CaseCondition_Type: ConditionType;
  CaseCondition_DueAt?: string | null;
  CaseCondition_AmountCents?: number | null;
  CaseCondition_Hours?: number | null;
  CaseCondition_Detail?: string | null;
}

export type UpdateConditionInput = Partial<CreateConditionInput> & {
  CaseCondition_CompletedAt?: string | null;
};

export default class ConditionsApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async list(ticketGuid: string): Promise<ConditionsResponse> {
    const response = await fetch(
      `${ConditionsApi.host()}/api/v1/manager/ticket/${ticketGuid}/conditions`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load the conditions on this case');
    return response.json();
  }

  static async create(
    ticketGuid: string,
    input: CreateConditionInput,
  ): Promise<CaseConditionDto> {
    const response = await fetch(
      `${ConditionsApi.host()}/api/v1/manager/ticket/${ticketGuid}/conditions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to record the condition');
    }
    return response.json();
  }

  static async update(
    ticketGuid: string,
    conditionGuid: string,
    input: UpdateConditionInput,
  ): Promise<CaseConditionDto> {
    const response = await fetch(
      `${ConditionsApi.host()}/api/v1/manager/ticket/${ticketGuid}/conditions/${conditionGuid}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to update the condition');
    }
    return response.json();
  }

  static async remove(ticketGuid: string, conditionGuid: string): Promise<void> {
    const response = await fetch(
      `${ConditionsApi.host()}/api/v1/manager/ticket/${ticketGuid}/conditions/${conditionGuid}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to delete the condition');
    }
  }
}
